import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, spots, starPath, vignette, type Painter } from '../../art/paint';

const TAU = Math.PI * 2;

type Pt = readonly [number, number];

interface Curtain {
  readonly x0: number;
  readonly x1: number;
  readonly y: (t: number) => number;
  readonly lift: (t: number) => number;
  readonly color: number;
  readonly top: number;
  readonly alpha: number;
}

interface Layout {
  readonly portrait: boolean;
  readonly curtains: readonly Curtain[];
  readonly ridge: readonly Pt[];
  readonly pines: readonly (readonly [number, number, number])[];
  readonly igloo: readonly [number, number, number];
  readonly lake: readonly [number, number, number, number];
  readonly drift: number;
  readonly star: Pt;
}

function hash(i: number, k: number): number {
  const s = Math.sin(i * 127.1 + k * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

function ease(v: number): number {
  const k = Math.min(1, Math.max(0, v));
  return k * k * (3 - 2 * k);
}

function layout(p: Painter, look: Look): Layout {
  const { w, h, unit } = p;
  const pal = look.palette;
  const green = pal.held;
  const ice = pal.incoming.circle;
  const portrait = w / h < 0.8;
  if (portrait) {
    const tall = h * 0.15;
    return {
      portrait,
      curtains: [
        {
          x0: -0.12 * w,
          x1: 0.78 * w,
          y: (t) => h * (0.305 - 0.075 * t + 0.012 * Math.sin(t * TAU * 1.1 + 0.4)),
          lift: (t) => tall * (0.85 + 0.2 * Math.sin(t * Math.PI * 1.7 + 0.6)),
          color: green,
          top: lerpColor(green, ice, 0.55),
          alpha: 0.13,
        },
        {
          x0: 0.28 * w,
          x1: 1.12 * w,
          y: (t) => h * (0.205 + 0.07 * t + 0.01 * Math.sin(t * TAU * 1.3 + 2.1)),
          lift: (t) => tall * (0.75 + 0.2 * Math.sin(t * Math.PI * 1.4 + 1.9)),
          color: lerpColor(green, ice, 0.4),
          top: ice,
          alpha: 0.11,
        },
      ],
      ridge: [
        [-0.05, 0.8],
        [0.1, 0.755],
        [0.26, 0.79],
        [0.44, 0.745],
        [0.62, 0.785],
        [0.8, 0.735],
        [0.95, 0.77],
        [1.06, 0.75],
      ],
      pines: [
        [0.08 * w, 0.885 * h, 0.14 * h],
        [0.2 * w, 0.89 * h, 0.1 * h],
        [0.3 * w, 0.895 * h, 0.07 * h],
        [0.92 * w, 0.885 * h, 0.115 * h],
      ],
      igloo: [0.7 * w, 0.88 * h, Math.min(0.13 * w, 0.055 * h)],
      lake: [0.48 * w, 0.845 * h, 0.3 * w, 0.016 * h],
      drift: 0.86 * h,
      star: [0.86 * w, 0.66 * h],
    };
  }
  const tall = unit * 0.42;
  return {
    portrait,
    curtains: [
      {
        x0: -0.08 * w,
        x1: 0.64 * w,
        y: (t) => h * (0.41 - 0.16 * t + 0.02 * Math.sin(t * TAU * 1.2 + 0.5)),
        lift: (t) => tall * (0.9 + 0.22 * Math.sin(t * Math.PI * 1.6 + 0.8)),
        color: green,
        top: lerpColor(green, ice, 0.55),
        alpha: 0.13,
      },
      {
        x0: 0.36 * w,
        x1: 1.08 * w,
        y: (t) => h * (0.24 + 0.15 * t + 0.015 * Math.sin(t * TAU * 1.4 + 2.2)),
        lift: (t) => tall * (0.8 + 0.2 * Math.sin(t * Math.PI * 1.3 + 1.7)),
        color: lerpColor(green, ice, 0.4),
        top: ice,
        alpha: 0.11,
      },
    ],
    ridge: [
      [-0.03, 0.78],
      [0.06, 0.66],
      [0.14, 0.73],
      [0.23, 0.69],
      [0.33, 0.8],
      [0.45, 0.84],
      [0.56, 0.815],
      [0.67, 0.83],
      [0.76, 0.72],
      [0.85, 0.65],
      [0.93, 0.71],
      [1.03, 0.67],
    ],
    pines: [
      [0.05 * w, 0.99 * h, unit * 0.62],
      [0.11 * w, 0.985 * h, unit * 0.46],
      [0.17 * w, 0.99 * h, unit * 0.34],
      [0.225 * w, 0.995 * h, unit * 0.24],
      [0.915 * w, 0.985 * h, unit * 0.4],
      [0.955 * w, 0.99 * h, unit * 0.54],
    ],
    igloo: [0.81 * w, 0.93 * h, unit * 0.17],
    lake: [0.5 * w, 0.92 * h, 0.24 * w, 0.022 * h],
    drift: 0.94 * h,
    star: [0.13 * w, 0.62 * h],
  };
}

function sky(p: Painter, look: Look): void {
  const ctx = p.ctx;
  const bg = look.palette.bg;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(bg, 0x000000, 0.35)));
  g.addColorStop(0.55, rgba(bg));
  g.addColorStop(1, rgba(hsl(look.hue, 40, 16)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
}

function stars(p: Painter, look: Look): void {
  const ctx = p.ctx;
  const pal = look.palette;
  for (let i = 0; i < 110; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h * 0.72;
    if (calmAt(p, x, y, 0.85)) continue;
    ctx.fillStyle = rgba(p.rng.chance(0.2) ? pal.incoming.circle : pal.rim, p.rng.range(0.15, 0.5));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.4, 1.2), 0, TAU);
    ctx.fill();
  }
}

function curtain(p: Painter, c: Curtain, fringe: number | null): void {
  const ctx = p.ctx;
  const soft = p.unit * 0.035;
  const step = Math.max(1, Math.round(p.w / 480));
  const edge = lerpColor(c.color, 0xffffff, 0.2);
  const mid = lerpColor(c.color, c.top, 0.5);
  const crown = fringe ?? c.top;
  const span = c.x1 - c.x0;
  const phase = (c.x0 / p.w) * 7;
  for (let x = Math.max(0, Math.floor(c.x0)); x < Math.min(p.w, c.x1); x += step) {
    const t = (x + step / 2 - c.x0) / span;
    const a = c.alpha * ease(t / 0.25) * ease((1 - t) / 0.25) * (0.86 + 0.14 * Math.sin(t * TAU * 1.6 + phase));
    if (a <= 0.002) continue;
    const y = c.y(t);
    const top = y - c.lift(t);
    const e = soft / (y + soft - top);
    const g = ctx.createLinearGradient(0, y + soft, 0, top);
    g.addColorStop(0, rgba(edge, 0));
    g.addColorStop(e, rgba(edge, a * 2.6));
    g.addColorStop(e + (1 - e) * 0.15, rgba(c.color, a * 1.9));
    g.addColorStop(e + (1 - e) * 0.5, rgba(mid, a * 1.1));
    g.addColorStop(e + (1 - e) * 0.8, rgba(crown, a * 0.55));
    g.addColorStop(1, rgba(c.top, 0));
    ctx.fillStyle = g;
    ctx.fillRect(x, top, step, y + soft - top);
  }
}

function ridge(p: Painter, look: Look, l: Layout): void {
  const ctx = p.ctx;
  const far = hsl(look.hue, 40, 16);
  const pts = l.ridge.map(([x, y]) => [x * p.w, y * p.h] as const);
  ctx.fillStyle = rgba(far);
  ctx.beginPath();
  ctx.moveTo(-10, p.h + 10);
  for (const [x, y] of pts) ctx.lineTo(x, y);
  ctx.lineTo(p.w + 10, p.h + 10);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(far, look.palette.rim, 0.32), 0.42);
  for (let i = 1; i < pts.length - 1; i++) {
    const [x, y] = pts[i]!;
    const [ax, ay] = pts[i - 1]!;
    const [bx, by] = pts[i + 1]!;
    if (y > ay || y > by) continue;
    const k = 0.32 + 0.1 * hash(i, 1);
    const lx = x + (ax - x) * k;
    const ly = y + (ay - y) * k;
    const rx = x + (bx - x) * k;
    const ry = y + (by - y) * k;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(rx, ry);
    ctx.lineTo(x + (rx - x) * 0.55, y + (ry - y) * 0.75 + p.unit * 0.012);
    ctx.lineTo(x, y + (ry - y) * 0.6);
    ctx.lineTo(x + (lx - x) * 0.5, y + (ly - y) * 0.85 + p.unit * 0.01);
    ctx.lineTo(lx, ly);
    ctx.closePath();
    ctx.fill();
  }
  ctx.strokeStyle = rgba(look.palette.rim, 0.12);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  pts.forEach(([x, y], i) => {
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
}

function lake(p: Painter, look: Look, l: Layout): void {
  const ctx = p.ctx;
  const pal = look.palette;
  const [x, y, rx, ry] = l.lake;
  ctx.fillStyle = rgba(lerpColor(pal.bg, pal.incoming.circle, 0.16));
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, TAU);
  ctx.fill();
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, TAU);
  ctx.clip();
  const g = ctx.createLinearGradient(x - rx, 0, x + rx, 0);
  g.addColorStop(0, rgba(pal.held, 0.16));
  g.addColorStop(0.5, rgba(pal.held, 0.04));
  g.addColorStop(1, rgba(pal.incoming.circle, 0.14));
  ctx.fillStyle = g;
  ctx.fillRect(x - rx, y - ry, rx * 2, ry * 2);
  ctx.strokeStyle = rgba(pal.rim, 0.18);
  ctx.lineWidth = 1.2;
  ctx.lineCap = 'round';
  for (let i = 0; i < 3; i++) {
    const cx = x + (hash(i, 7) - 0.5) * rx * 1.2;
    const cy = y + (hash(i, 8) - 0.5) * ry;
    const len = rx * (0.15 + 0.15 * hash(i, 9));
    ctx.beginPath();
    ctx.moveTo(cx - len, cy);
    ctx.lineTo(cx + len, cy);
    ctx.stroke();
  }
  ctx.restore();
  ctx.strokeStyle = rgba(pal.rim, 0.22);
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, Math.PI * 1.05, Math.PI * 1.95);
  ctx.stroke();
}

function drifts(p: Painter, look: Look, l: Layout, front: boolean): void {
  const ctx = p.ctx;
  const far = hsl(look.hue, 40, 16);
  const tone = lerpColor(far, look.palette.rim, front ? 0.2 : 0.13);
  const base = front ? l.drift + p.h * 0.03 : l.drift;
  const bump = (front ? 0.08 : 0.12) * p.unit;
  ctx.fillStyle = rgba(tone);
  ctx.beginPath();
  ctx.moveTo(-10, p.h + 10);
  const n = 7;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const x = -0.05 * p.w + t * 1.1 * p.w;
    const side = Math.abs(t - 0.5) * 2;
    const y = base - bump * (0.4 + 0.9 * side * side) * (0.7 + 0.5 * hash(i, front ? 3 : 2));
    if (i === 0) ctx.lineTo(x, y);
    else {
      const px = -0.05 * p.w + ((i - 0.5) / n) * 1.1 * p.w;
      ctx.quadraticCurveTo(px, y - bump * 0.5, x, y);
    }
  }
  ctx.lineTo(p.w + 10, p.h + 10);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(tone, look.palette.rim, 0.35), 0.35);
  ctx.lineWidth = 1.3;
  ctx.stroke();
}

function pine(p: Painter, look: Look, x: number, base: number, tall: number, i: number): void {
  const ctx = p.ctx;
  const pal = look.palette;
  const dark = lerpColor(pal.bg, pal.ink, 0.45);
  const snow = lerpColor(hsl(look.hue, 40, 16), pal.rim, 0.34);
  const half = tall * 0.24;
  ctx.fillStyle = rgba(dark);
  ctx.fillRect(x - tall * 0.025, base - tall * 0.12, tall * 0.05, tall * 0.14);
  const tiers = 4;
  for (let k = 0; k < tiers; k++) {
    const top = base - tall + (k * tall * 0.78) / tiers;
    const bot = base - tall * 0.08 - ((tiers - 1 - k) * tall * 0.62) / tiers;
    const wide = half * (0.45 + (0.55 * (k + 1)) / tiers);
    const lean = (hash(i, k) - 0.5) * wide * 0.12;
    ctx.fillStyle = rgba(dark);
    ctx.beginPath();
    ctx.moveTo(x + lean * 0.3, top);
    ctx.lineTo(x + wide + lean, bot);
    ctx.quadraticCurveTo(x, bot - tall * 0.05, x - wide + lean, bot);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = rgba(snow, 0.55);
    ctx.beginPath();
    ctx.moveTo(x + wide * 0.92 + lean, bot - tall * 0.008);
    ctx.quadraticCurveTo(x + wide * 0.4, bot - tall * 0.07, x - wide * 0.1, bot - tall * 0.045);
    ctx.quadraticCurveTo(x + wide * 0.45, bot - tall * 0.03, x + wide * 0.92 + lean, bot - tall * 0.008);
    ctx.fill();
  }
  ctx.fillStyle = rgba(snow, 0.6);
  ctx.beginPath();
  ctx.ellipse(x, base - tall + tall * 0.03, tall * 0.025, tall * 0.02, 0, 0, TAU);
  ctx.fill();
}

function igloo(p: Painter, look: Look, l: Layout): void {
  const ctx = p.ctx;
  const pal = look.palette;
  const [x, y, r] = l.igloo;
  const far = hsl(look.hue, 40, 16);
  const dome = lerpColor(far, pal.rim, 0.24);
  const line = lerpColor(dome, pal.ink, 0.45);
  ctx.fillStyle = rgba(dome);
  ctx.beginPath();
  ctx.arc(x, y, r, Math.PI, TAU);
  ctx.closePath();
  ctx.fill();
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, r, Math.PI, TAU);
  ctx.closePath();
  ctx.clip();
  ctx.strokeStyle = rgba(line, 0.32);
  ctx.lineWidth = Math.max(1, r * 0.025);
  const rows = 4;
  for (let k = 1; k < rows; k++) {
    const ry = y - (k / rows) * r;
    const half = Math.sqrt(Math.max(0, r * r - (ry - y) ** 2));
    ctx.beginPath();
    ctx.moveTo(x - half, ry);
    ctx.quadraticCurveTo(x, ry - r * 0.04, x + half, ry);
    ctx.stroke();
    const yb = y - ((k - 1) / rows) * r;
    const bricks = rows + 1 - k;
    for (let b = 0; b <= bricks; b++) {
      const bx = x - half + ((b + (k % 2) * 0.5 + 0.15 * hash(k, b)) / (bricks + 0.5)) * half * 2;
      ctx.beginPath();
      ctx.moveTo(bx, ry);
      ctx.lineTo(bx + (bx - x) * 0.05, yb);
      ctx.stroke();
    }
  }
  ctx.restore();
  ctx.strokeStyle = rgba(lerpColor(dome, pal.rim, 0.35), 0.5);
  ctx.lineWidth = Math.max(1.2, r * 0.03);
  ctx.beginPath();
  ctx.arc(x, y, r, Math.PI * 1.08, Math.PI * 1.6);
  ctx.stroke();
  const tx = x - r * 0.62;
  const tr = r * 0.38;
  ctx.fillStyle = rgba(lerpColor(dome, pal.rim, 0.08));
  ctx.beginPath();
  ctx.ellipse(tx, y, tr * 1.15, tr, 0, Math.PI, TAU);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(line, 0.4);
  ctx.lineWidth = Math.max(1, r * 0.025);
  ctx.stroke();
  glowDot(ctx, tx - tr * 0.25, y - tr * 0.35, tr * 1.3, pal.heldLight, 0.22);
  ctx.fillStyle = rgba(pal.ink, 0.92);
  ctx.beginPath();
  ctx.ellipse(tx - tr * 0.25, y, tr * 0.55, tr * 0.68, 0, Math.PI, TAU);
  ctx.closePath();
  ctx.fill();
}

function flakes(p: Painter, look: Look): void {
  const ctx = p.ctx;
  ctx.lineCap = 'round';
  for (const at of spots(p, 9, 1.0, 24, p.unit * 0.22)) {
    const r = p.rng.range(3, 6.5);
    const rot = p.rng.float() * TAU;
    ctx.strokeStyle = rgba(look.palette.rim, p.rng.range(0.3, 0.5));
    ctx.lineWidth = 1.1;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = rot + (i / 6) * TAU;
      ctx.moveTo(at.x, at.y);
      ctx.lineTo(at.x + Math.cos(a) * r, at.y + Math.sin(a) * r);
    }
    ctx.stroke();
  }
}

function poleStar(p: Painter, look: Look, l: Layout): void {
  const ctx = p.ctx;
  const [x, y] = l.star;
  const r = Math.max(6, p.unit * 0.028);
  glowDot(ctx, x, y, r * 4, look.palette.spot, 0.2);
  ctx.fillStyle = rgba(lerpColor(look.palette.rim, look.palette.spot, 0.3), 0.6);
  starPath(ctx, x, y, r, 0.22, 4, 0);
  ctx.fill();
}

export function paint(p: Painter, look: Look): void {
  const l = layout(p, look);
  sky(p, look);
  stars(p, look);
  const fringe = p.level >= 3 ? look.palette.spot : null;
  for (const c of l.curtains) curtain(p, c, fringe);
  if (p.level >= 4) poleStar(p, look, l);
  ridge(p, look, l);
  drifts(p, look, l, false);
  if (p.level >= 2) lake(p, look, l);
  l.pines.forEach(([x, base, tall], i) => {
    pine(p, look, x, base, tall, i);
  });
  igloo(p, look, l);
  drifts(p, look, l, true);
  flakes(p, look);
  calmCenter(p, look.palette.bg, 0.6, 1.15);
  grain(p, 0.04, true);
  vignette(p, look.palette.ink, 0.45);
}
